import { createSlice } from "@reduxjs/toolkit";
import {
  getResources,
  createResource,
  updateResource,
  deleteResource,
} from "../services/resources";

interface ResourcesState {
  resourceStatus: string;
  showLoadingIndicator: boolean;
  resourcesData: unknown[];
  errorMessage: string;
}

const initialState: ResourcesState = {
  resourceStatus: "idle",
  showLoadingIndicator: false,
  resourcesData: [],
  errorMessage: "",
};

const resourcesSlice = createSlice({
  name: "resources",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getResources.pending, (state) => {
        state.resourceStatus = "pending";
        state.showLoadingIndicator = true;
        state.errorMessage = "";
      })
      .addCase(getResources.fulfilled, (state, action) => {
        const payload = action.payload as
          | { data?: unknown[] }
          | null
          | undefined;
        state.resourceStatus = "success";
        state.showLoadingIndicator = false;
        state.resourcesData = payload?.data ?? [];
        state.errorMessage = "";
      })
      .addCase(getResources.rejected, (state, action) => {
        state.resourceStatus = "rejected";
        state.showLoadingIndicator = false;
        state.resourcesData = [];
        const errPayload = action.payload as
          | { response?: { data?: { message?: string } }; message?: string }
          | string
          | undefined;
        state.errorMessage =
          (typeof errPayload === "object"
            ? errPayload?.response?.data?.message
            : undefined) ||
          (typeof errPayload === "string" ? errPayload : undefined) ||
          "Error loading resources";
      })
      .addCase(createResource.pending, (state) => {
        state.resourceStatus = "create:pending";
        state.errorMessage = "";
      })
      .addCase(createResource.fulfilled, (state) => {
        state.resourceStatus = "create:success";
        state.errorMessage = "";
      })
      .addCase(createResource.rejected, (state, action) => {
        state.resourceStatus = "create:rejected";
        const errPayload = action.payload as
          | { response?: { data?: { message?: string } } }
          | string
          | undefined;
        state.errorMessage =
          (typeof errPayload === "object"
            ? errPayload?.response?.data?.message
            : undefined) ||
          (typeof errPayload === "string" ? errPayload : undefined) ||
          "Error creating resource";
      })
      .addCase(updateResource.pending, (state) => {
        state.resourceStatus = "update:pending";
        state.errorMessage = "";
      })
      .addCase(updateResource.fulfilled, (state) => {
        state.resourceStatus = "update:success";
        state.errorMessage = "";
      })
      .addCase(updateResource.rejected, (state, action) => {
        state.resourceStatus = "update:rejected";
        const errPayload = action.payload as
          | { response?: { data?: { message?: string } } }
          | string
          | undefined;
        state.errorMessage =
          (typeof errPayload === "object"
            ? errPayload?.response?.data?.message
            : undefined) ||
          (typeof errPayload === "string" ? errPayload : undefined) ||
          "Error updating resource";
      })
      .addCase(deleteResource.pending, (state) => {
        state.resourceStatus = "delete:pending";
        state.errorMessage = "";
      })
      .addCase(deleteResource.fulfilled, (state) => {
        state.resourceStatus = "delete:success";
        state.errorMessage = "";
      })
      .addCase(deleteResource.rejected, (state, action) => {
        state.resourceStatus = "delete:rejected";
        const errPayload = action.payload as
          | { response?: { data?: { message?: string } } }
          | string
          | undefined;
        state.errorMessage =
          (typeof errPayload === "object"
            ? errPayload?.response?.data?.message
            : undefined) ||
          (typeof errPayload === "string" ? errPayload : undefined) ||
          "Error deleting resource";
      });
  },
});

export default resourcesSlice.reducer;
